import { useCallback, useEffect, useState } from 'react';
import { AiSummaryCard } from '@/features/share/components/ai-summary-card';
import { generateShareLink, getLatestCachedSummary } from '@/features/share/lib/shareApi';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: string; createdAt: string }
  | { kind: 'empty' }
  | { kind: 'error' };

interface Props {
  profileId: string;
  profileName: string;
  eventCount: number;
  latestEventDate?: string | null;
  onCitationClick: (eventId: string) => void;
}

export function ProfileSummaryTab({
  profileId,
  profileName,
  eventCount,
  latestEventDate,
  onCitationClick,
}: Props) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const meta = await getLatestCachedSummary(profileId);
      setState(
        meta
          ? { kind: 'ready', summary: meta.summary, createdAt: meta.createdAt }
          : { kind: 'empty' },
      );
    } catch {
      setState({ kind: 'error' });
    }
  }, [profileId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleGenerate() {
    setGenerating(true);
    setGenError(null);
    const result = await generateShareLink(profileId, { expiresInDays: 7 });
    setGenerating(false);
    if (result.success) {
      load();
    } else {
      setGenError('Could not generate summary. Please try again.');
    }
  }

  if (state.kind === 'loading') {
    return (
      <div className="card overflow-hidden animate-pulse" aria-busy="true">
        <div className="h-14 bg-canvas border-b border-border" />
        <div className="px-5 py-5 space-y-3">
          <div className="h-3 bg-border rounded-full w-full" />
          <div className="h-3 bg-canvas rounded-full w-5/6" />
          <div className="h-3 bg-canvas rounded-full w-4/6 mt-4" />
          <div className="h-3 bg-border rounded-full w-full" />
          <div className="h-3 bg-canvas rounded-full w-3/4 mt-4" />
          <div className="h-3 bg-canvas rounded-full w-5/6" />
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="card p-6 text-center">
        <p className="text-flag-block mb-3">Couldn't load summary.</p>
        <button type="button" onClick={load} className="btn-secondary">
          Try again
        </button>
      </div>
    );
  }

  if (state.kind === 'empty') {
    return (
      <div className="card p-6 text-center">
        <span
          aria-hidden="true"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft mb-3"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5 text-accent"
          >
            <path d="m12 3 2.5 5.5L20 11l-5.5 2.5L12 19l-2.5-5.5L4 11l5.5-2.5L12 3z" />
          </svg>
        </span>
        <p className="font-medium mb-1">No AI summary yet</p>
        <p className="text-sm text-ink-muted mb-4">
          Generate a clinical overview of {profileName}
          's records using AI.
        </p>
        {genError && (
          <p className="text-sm text-flag-block mb-3" role="alert">{genError}</p>
        )}
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="btn-primary inline-flex"
        >
          {generating ? 'Generating…' : 'Generate Summary'}
        </button>
      </div>
    );
  }

  const hasNewRecords =
    latestEventDate != null && latestEventDate > state.createdAt;

  return (
    <div className="space-y-3">
      <AiSummaryCard
        text={state.summary}
        recordCount={eventCount}
        createdAt={state.createdAt}
        onCitationClick={onCitationClick}
      />
      {hasNewRecords && (
        <div className="flex items-center gap-3 card px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ink">New records added</p>
            <p className="text-xs text-ink-muted">Regenerate to include them in the summary.</p>
          </div>
          {genError && (
            <p className="text-xs text-flag-block shrink-0" role="alert">{genError}</p>
          )}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="btn-secondary shrink-0 text-sm"
          >
            {generating ? 'Generating…' : 'Regenerate'}
          </button>
        </div>
      )}
    </div>
  );
}
